/**
 * "EN_ROUTE" as "En route". Every status reads in words, on the desk and the
 * phone alike: a column of capitals shouts, and the Call Log's own group
 * headings already said "On scene" over pills that said "ON SCENE".
 */
export const sentenceCase = (status: string): string =>
  status
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

/*
 * The phone's designs have their own words for two statuses: the calls list
 * (Figma 143:7883) and the Call Status sheet (344:64824) say "Ready" for a
 * new call and "Dispatching" for a dispatched one. The dashboard keeps its
 * own words, from its own designs.
 */
const CALL_STATUS_WORDS: Record<string, string> = {
  NEW: 'Ready',
  DISPATCHED: 'Dispatching',
};

/**
 * A call's status in the phone's words. One table, so the list row, the
 * call's header, the active-call pill and the status sheet cannot say the
 * same status two ways. A status the designs have no word for reads as the
 * API's, in sentence case: "ON_SCENE" is "On scene".
 *
 * An own-property check for the reason toneForStatus gives: a bare lookup
 * of "constructor" walks the prototype chain.
 */
export function callStatusLabel(status: string): string {
  return Object.prototype.hasOwnProperty.call(CALL_STATUS_WORDS, status)
    ? CALL_STATUS_WORDS[status]
    : sentenceCase(status);
}

/** The street, without the city: "La Jolla Cir" from "La Jolla Cir, San Diego". */
export const firstLine = (address: string | null | undefined): string =>
  address?.split(',')[0]?.trim() || 'Call';

/** The town, under the street in a list: "San Diego" from "La Jolla Cir, San Diego, CA". */
export const locality = (
  address: string | null | undefined
): string | undefined => address?.split(',')[1]?.trim() || undefined;

/**
 * A call's weight class from its tow type, for the bars in a list row. Only
 * the three duty classes have one; a motorcycle or a container is a kind
 * of tow, not a weight, and draws no bars.
 */
export function weightClassOf(
  towType: string | null | undefined
): 'light' | 'medium' | 'heavy' | undefined {
  if (towType === 'LIGHT') return 'light';
  if (towType === 'MEDIUM') return 'medium';
  if (towType === 'HEAVY') return 'heavy';
  return undefined;
}
